import { Link } from "@inertiajs/react";
import {
  Brain,
  Clock,
  Crown,
  Loader2,
  Monitor,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import {
  AnalysisProgressBar,
  type AnalysisStatus,
} from "@/components/analysis/AnalysisProgressBar.tsx";
import { CompanyLogo } from "@/components/CompanyLogo.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { useInsightRequestChannel } from "@/hooks/useInsightRequestChannel";
import { bizminer_app_pillar_insight_insight_request_path } from "@/routes";
import type { BizminerInsightRequestDashboard } from "@/types/serializers";

const iconMap = {
  users: Users,
  "user-check": UserCheck,
  monitor: Monitor,
  crown: Crown,
  brain: Brain,
  zap: Zap,
};

export function PendingInsightRequestCard({
  request: initialRequest,
}: {
  request: BizminerInsightRequestDashboard;
}) {
  const { insightRequest } = useInsightRequestChannel(initialRequest);
  const request = insightRequest
    ? { ...initialRequest, ...insightRequest }
    : initialRequest;

  const IconComponent =
    iconMap[request.pillar_insight.icon as keyof typeof iconMap] || Users;

  const progressStatus: AnalysisStatus =
    (request.engine_status as AnalysisStatus) || "collecting_data";

  const timeAgo = new Date(request.created_at).toLocaleString();

  return (
    <Link
      href={bizminer_app_pillar_insight_insight_request_path({
        account_id: request.account.id,
        pillar_insight_id: request.pillar_insight.id,
      })}
    >
      <Card className="cursor-pointer hover:shadow-lg transition-all hover:border-primary">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <CompanyLogo
                logoUrl={request.account.logo_url}
                name={request.account.name}
                className="size-10 rounded-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base truncate">
                  {request.account.name}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <IconComponent className="h-3.5 w-3.5 text-primary" />
                  <span className="truncate">
                    {request.pillar_insight.title}
                  </span>
                </CardDescription>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="flex items-center gap-1 flex-shrink-0"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              {request.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <AnalysisProgressBar status={progressStatus} />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Started {timeAgo}</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export const PendingRequestsOverview = ({
  pendingRequests,
}: {
  pendingRequests: BizminerInsightRequestDashboard[];
}) => {
  if (pendingRequests.length === 0) {
    return;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold">Pending Analysis</h2>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {pendingRequests.length} running
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pendingRequests.map((request) => (
          <PendingInsightRequestCard key={request.id} request={request} />
        ))}
      </div>
    </div>
  );
};
